import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { projects } from '../data/projects'
import { skills } from '../data/skills'
import { Icon } from '../components/Icon'
import { Text } from '../components/Text'
import { ProjectCard } from '../components/ProjectCard'
import Carousel from '../components/Carousel'

const HomeContainer = styled(motion.div)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
`

const HeroSection = styled(motion.section)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 1rem;
  position: relative;
`

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 20px rgba(78, 205, 196, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`

const Subtitle = styled(motion.p)`
  font-size: 1.8rem;
  color: #ffffff;
  margin-bottom: 2rem;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`

const Description = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  line-height: 1.8;
  margin-bottom: 3rem;
`

const Section = styled(motion.section)`
  width: 100%;
  max-width: 1200px;
  padding: 4rem 2rem;
  margin-top: 2rem;
`

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const ProjectsSection = styled(Section)`
  position: relative;
  overflow: hidden;
  padding: 1rem 0;
`

const CarouselWrapper = styled.div`
  width: 100%;
  height: 500px;
  margin: 2rem 0;
`

const ProjectCardWrapper = styled(motion.div)<{ $status?: string }>`
  background: #0a0a0a;
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 350px;
  margin: 0 1rem;

  &:hover {
    transform: translateY(-5px);
    background: #0a0a0a;
    box-shadow: 0 10px 30px
      ${({ $status }) =>
        $status === 'Featured'
          ? '#4ecdc4'
          : $status === 'In Development'
          ? '#ffa500'
          : $status === 'Coming Soon'
          ? '#ff0000'
          : '#0a0a0a'};
  }
`

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }
`

const SkillCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SkillTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SkillBadges = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const SkillLevel = styled.div<{ $level: string }>`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  display: inline-block;
  background: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return 'rgba(78, 205, 196, 0.1)'
      case 'Advanced':
        return 'rgba(255, 171, 0, 0.1)'
      case 'Intermediate':
        return 'rgba(255, 107, 107, 0.1)'
      default:
        return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  color: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return '#4ECDC4'
      case 'Advanced':
        return '#FFAB00'
      case 'Intermediate':
        return '#ff6b6b'
      default:
        return '#ffffff'
    }
  }};
`

const ExperienceBadge = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  display: inline-block;

  &::after {
    content: 'years';
    font-size: 0.8rem;
    opacity: 0.7;
    margin-left: 0.3rem;
  }
`

const ViewAllButton = styled(motion.button)`
  margin: 2rem auto;
  display: block;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(78, 205, 196, 0.3);
  }
`

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
}

// Color map for skill icons
const skillIconColorMap: Record<string, string> = {
  javascript: '#f7df1e',
  typescript: '#3178c6',
  react: '#61dafb',
  node: '#3c873a',
  mongodb: '#47a248',
  aws: '#ff9900',
  git: '#f34f29',
  docker: '#2496ed',
  graphql: '#e535ab',
  html5: '#ff5733',
  css: '#3498db',
  allIcons: '#4ECDC4',
}

export function HomePage() {
  const navigate = useNavigate()
  const featuredSkills = skills.filter((skill) => skill.featured).slice(0, 6)

  const carouselItems = projects.map((project) => ({
    id: project.id,
    content: (
      <ProjectCardWrapper
        key={project.id}
        $status={project.status}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        onClick={() => navigate(`/projects#${project.id}`)}>
        <ProjectCard project={project} variant='minimal' />
      </ProjectCardWrapper>
    ),
  }))

  return (
    <HomeContainer>
      <HeroSection
        variants={containerVariants}
        initial='hidden'
        animate='visible'>
        <Title variants={itemVariants}>Hi, I'm Mr. Zee</Title>
        <Subtitle variants={itemVariants}>
          Full Stack Developer & UI/UX Enthusiast
        </Subtitle>
        <Description variants={itemVariants}>
          I specialize in creating modern, responsive web applications with a
          focus on user experience and clean code. With expertise in both
          frontend and backend development, I bring ideas to life through
          elegant solutions and innovative design.
        </Description>
      </HeroSection>

      <ProjectsSection
        variants={containerVariants}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}>
        <SectionTitle variants={itemVariants}>My Projects</SectionTitle>
        <CarouselWrapper>
          <Carousel
            items={carouselItems}
            autoPlay={true}
            interval={5000}
            showDots={true}
            showArrows={true}
          />
        </CarouselWrapper>
      </ProjectsSection>

      <Section
        variants={containerVariants}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}>
        <SectionTitle variants={itemVariants}>Skills & Expertise</SectionTitle>
        <SkillsGrid>
          {featuredSkills.map((skill) => (
            <SkillCard
              key={skill.id}
              variants={itemVariants}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}>
              <SkillHeader>
                <SkillTitle>
                  <Icon
                    icon={skill.icon}
                    size={32}
                    color={skillIconColorMap[skill.id] || '#fff'}
                  />
                  <Text
                    text={skill.name}
                    fontSize='1.2rem'
                    fontWeight='bold'
                    color='#ffffff'
                    margin='0'
                  />
                </SkillTitle>
              </SkillHeader>
              <SkillBadges>
                <SkillLevel $level={skill.level}>{skill.level}</SkillLevel>
                <ExperienceBadge>{skill.yearsOfExperience}</ExperienceBadge>
              </SkillBadges>
            </SkillCard>
          ))}
        </SkillsGrid>
        <ViewAllButton
          onClick={() => navigate('/skills')}
          variants={itemVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}>
          View All Skills →
        </ViewAllButton>
      </Section>
    </HomeContainer>
  )
}
